import BunkeyAPI from "../api/BunkeyAPI";
import { mapAssetFromApi } from "../utils";
import {
  CLEAR_RIGHTS_ASSIGN_ERROR,
  GET_RIGHTS_LIST,
  GET_RIGHTS_LIST_SUCCESS,
  GET_RIGHTS_LIST_FAILURE,
  GET_RIGHTS_TREE_NODE,
  GET_RIGHTS_TREE_NODE_SUCCESS,
  GET_RIGHTS_TREE_NODE_FAILURE,
  GET_RIGHTS_SUMMARY,
  GET_RIGHTS_SUMMARY_SUCCESS,
  GET_RIGHTS_SUMMARY_FAILURE,
  ASSIGN_RIGHTS_MANY,
  ASSIGN_RIGHTS_MANY_SUCCESS,
  ASSIGN_RIGHTS_MANY_FAILURE,
  REVERT_RIGHTS_OPERATION,
  REVERT_RIGHTS_OPERATION_SUCCESS,
  REVERT_RIGHTS_OPERATION_FAILURE,
  EXPORT_RIGHTS_REPORT,
  EXPORT_RIGHTS_REPORT_SUCCESS,
  EXPORT_RIGHTS_REPORT_FAILURE,
  CLEAR_RIGHTS_LAST_OPERATION,
  OPEN_RIGHTS_VIEWER,
  OPEN_RIGHTS_VIEWER_SUCCESS,
  OPEN_RIGHTS_VIEWER_FAILURE,
  CLOSE_RIGHTS_VIEWER
} from "../constants/rights-report-types";

// Clave del nodo del árbol en el reducer: "root" para la raíz, el id de la
// carpeta para cualquier otro nivel. Mismo criterio que usa el back para
// distinguir "sin parentId" de "una carpeta puntual".
export const ROOT_NODE_KEY = "root";

/**
 * Un nivel del árbol de carpetas (DES-207 ampliado).
 *
 * @param {String} nodeKey clave bajo la que guardar este nodo en el reducer
 *   (ROOT_NODE_KEY o el id de la carpeta que se está expandiendo)
 * @param {String|null} parentId id de la carpeta a expandir, o null para la raíz
 */
/**
 * Contador de peticiones del árbol.
 *
 * Cambiar de filtro dispara una petición nueva del nodo raíz sin cancelar la
 * anterior, así que dos clics seguidos —en un KPI, por ejemplo— dejaban dos
 * respuestas en vuelo y el árbol se quedaba con la que llegara última, que
 * podía ser la del filtro viejo. De ahí que el mismo filtro diera resultados
 * distintos según el orden en que respondiera la red.
 *
 * Cada petición lleva su número; el reducer descarta las que ya no son la
 * última de ese nodo.
 */
// Tope de piezas que se cargan para poder navegar con las flechas. Una carpeta
// puede tener miles; traerlas todas sólo para poder pasar a la siguiente sería
// desproporcionado.
const VIEWER_LIMIT = 200;

let treeRequestSeq = 0;

export const getRightsTreeNode = (
  workclient,
  userId,
  nodeKey,
  parentId,
  rightsState,
  types
) => {
  return dispatch => {
    treeRequestSeq += 1;
    const requestId = treeRequestSeq;
    dispatch({ type: GET_RIGHTS_TREE_NODE, payload: { nodeKey, requestId } });
    BunkeyAPI({
      url: `clients/${workclient}/rights/report/tree`,
      method: "GET",
      params: {
        userid: userId,
        parentId: parentId || undefined,
        rightsState: rightsState || undefined,
        // El backend acepta lista o CSV y descarta lo que no reconoce; se manda
        // CSV para no depender de cómo serialice arrays el cliente HTTP.
        types: types && types.length ? types.join(",") : undefined
      }
    })
      .then(resp => {
        dispatch({
          type: GET_RIGHTS_TREE_NODE_SUCCESS,
          payload: {
            nodeKey,
            requestId,
            folders: resp.data.folders,
            files: resp.data.files
          }
        });
      })
      .catch(err => {
        dispatch({
          type: GET_RIGHTS_TREE_NODE_FAILURE,
          payload: { nodeKey, requestId, error: err }
        });
      });
  };
};

/**
 * Las 4 tarjetas de arriba (pin 2). Se pide con `limit=1` para no traer
 * filas: `summary` sale del mismo $facet que el reporte plano, así que su
 * costo no depende de la página pedida.
 */
export const getRightsSummary = (workclient, userId, types) => {
  return dispatch => {
    dispatch({ type: GET_RIGHTS_SUMMARY });
    BunkeyAPI({
      url: `clients/${workclient}/rights/report`,
      method: "GET",
      params: {
        userid: userId,
        limit: 1,
        page: 1,
        // Los KPI tienen que contar sobre el mismo universo que la lista y el
        // árbol: si el filtro por tipo no llegara acá, las tarjetas dirían un
        // total y el panel de abajo mostraría otro.
        types: types && types.length ? types.join(",") : undefined
      }
    })
      .then(resp => {
        dispatch({
          type: GET_RIGHTS_SUMMARY_SUCCESS,
          payload: {
            summary: resp.data.summary,
            total: resp.data.total,
            outOfModule: resp.data.outOfModule,
            updatedAt: resp.data.updatedAt || null
          }
        });
      })
      .catch(err => {
        dispatch({ type: GET_RIGHTS_SUMMARY_FAILURE, payload: err });
      });
  };
};

/**
 * Asignación (o quita) de derechos en lote, sobre una mezcla de carpetas y
 * archivos. `rights` es el mismo shape que emite Copyright.js.
 */
export const assignRightsMany = (workclient, userId, objectIds, rights, intl) => {
  return dispatch => {
    dispatch({ type: ASSIGN_RIGHTS_MANY });
    BunkeyAPI({
      url: `clients/${workclient}/rights/assign`,
      method: "PUT",
      data: { userid: userId, objects: objectIds, ...rights },
      // Un lote grande escribe de a 1.000 y además arma la bitácora de cada
      // asset: con el tope en 25.000 la operación puede pasar el minuto.
      timeout: 300000
    })
      .then(resp => {
        dispatch({
          type: ASSIGN_RIGHTS_MANY_SUCCESS,
          payload: { result: resp.data, intl }
        });
      })
      .catch(err => {
        dispatch({ type: ASSIGN_RIGHTS_MANY_FAILURE, payload: err });
      });
  };
};

/** Revierte una operación de lote completa por su operationId. */
export const revertRightsOperation = (workclient, userId, operationId) => {
  return dispatch => {
    dispatch({ type: REVERT_RIGHTS_OPERATION });
    BunkeyAPI({
      url: `clients/${workclient}/rights/revert`,
      method: "PUT",
      data: { userid: userId, operationId }
    })
      .then(resp => {
        dispatch({
          type: REVERT_RIGHTS_OPERATION_SUCCESS,
          payload: { result: resp.data }
        });
      })
      .catch(err => {
        dispatch({ type: REVERT_RIGHTS_OPERATION_FAILURE, payload: err });
      });
  };
};

/**
 * Limpia el error de la última asignación.
 *
 * El error vive en el store, así que sin esto el banner de un intento fallido
 * seguía visible al reabrir el modal con otra selección —por ejemplo, tras
 * fallar con un lote muy grande y volver con uno más chico—, dando a entender
 * que la selección nueva también había fallado.
 */
export const clearRightsAssignError = () => ({
  type: CLEAR_RIGHTS_ASSIGN_ERROR
});

/**
 * Vista de lista: filas del reporte plano, paginadas y ordenadas.
 *
 * El endpoint ya existía y devolvía todo esto; el Gestor sólo lo usaba con
 * limit=1 para sacarle el `summary` a los KPI y descartaba las filas. Acá se
 * consume de verdad, que es lo que el árbol no puede dar: todo lo vencido de la
 * cuenta junto, sin recorrer carpeta por carpeta.
 */
export const getRightsList = (
  workclient,
  userId,
  { page = 1, limit = 50, orderBy = "-createdAt", rightsState, types } = {}
) => {
  return dispatch => {
    dispatch({ type: GET_RIGHTS_LIST });
    BunkeyAPI({
      url: `clients/${workclient}/rights/report`,
      method: "GET",
      params: {
        userid: userId,
        page,
        limit,
        orderBy,
        rightsState: rightsState || undefined,
        types: types && types.length ? types.join(",") : undefined
      }
    })
      .then(resp => {
        dispatch({
          type: GET_RIGHTS_LIST_SUCCESS,
          payload: {
            rows: resp.data.collection || [],
            total: resp.data.total || 0,
            page,
            limit,
            orderBy
          }
        });
      })
      .catch(err => {
        dispatch({ type: GET_RIGHTS_LIST_FAILURE, payload: err });
      });
  };
};

/**
 * Export CSV del reporte completo (no sólo el nivel del árbol visible),
 * respetando los filtros activos. Mismo patrón blob que Shared.js:278-301,
 * pero por BunkeyAPI para que viaje el JWT — el endpoint es del gateway, no
 * un link directo a S3.
 */
export const exportRightsReport = (workclient, userId, filtros = {}) => {
  return dispatch => {
    dispatch({ type: EXPORT_RIGHTS_REPORT });
    BunkeyAPI({
      url: `clients/${workclient}/rights/report/export`,
      method: "GET",
      params: { userid: userId, ...filtros },
      responseType: "blob",
      // El backend transmite el CSV completo. El timeout evita que el botón
      // quede cargando indefinidamente si el upstream se corta o no responde.
      timeout: 120000
    })
      .then(resp => {
        const disposition = resp.headers["content-disposition"];
        const match = disposition && disposition.match(/filename="?([^"]+)"?/);
        const filename = (match && match[1]) || "derechos_de_uso.csv";

        const url = window.URL.createObjectURL(new Blob([resp.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        dispatch({ type: EXPORT_RIGHTS_REPORT_SUCCESS });
      })
      .catch(err => {
        dispatch({ type: EXPORT_RIGHTS_REPORT_FAILURE, payload: err });
      });
  };
};

export const clearRightsLastOperation = () => ({
  type: CLEAR_RIGHTS_LAST_OPERATION
});

/**
 * Abre un archivo del Gestor en el visor de assets.
 *
 * Trae la CARPETA completa, no la pieza suelta, por dos motivos:
 *
 * 1. Las flechas de anterior/siguiente del panel se mueven dentro de `data`;
 *    con un solo elemento no tienen a dónde ir.
 * 2. El panel no lee el objeto crudo de la API: espera `user` como objeto y la
 *    `matrix` de permisos resuelta. Ese mapeo es el mismo que usan las demás
 *    colecciones y vive en utils.mapAssetFromApi.
 *
 * El árbol ya sabe de qué carpeta cuelga el archivo, así que en general es una
 * sola consulta. En la raíz el nodo no tiene id de carpeta y hay que leer la
 * cadena de ancestros de la propia pieza para averiguarla.
 */
export const openRightsViewer = (workclient, asset, nodeKey) => {
  return async dispatch => {
    dispatch({ type: OPEN_RIGHTS_VIEWER });
    try {
      let folderId = nodeKey && nodeKey !== "root" ? nodeKey : null;

      if (!folderId) {
        const uno = await BunkeyAPI({
          url: `/clients/${workclient}/objects/${asset._id}`,
          method: "GET"
        });
        const padres = (uno.data && uno.data.parents) || [];
        folderId = padres.length > 0 ? padres[padres.length - 1] : null;
      }

      if (!folderId) throw new Error("no se pudo resolver la carpeta");

      const resp = await BunkeyAPI({
        url: `/clients/${workclient}/folders/${folderId}?page=1&limit=${VIEWER_LIMIT}&orderBy=name&isShared=false`,
        method: "GET"
      });

      const piezas = (resp.data || [])
        .filter(r => r.type !== "folder" && r.type !== "bunker")
        .map(mapAssetFromApi);

      // Si la carpeta tiene más piezas que el tope, la buscada puede no venir
      // en esta página. Antes que abrir el archivo equivocado, se abre sola.
      const estaIncluida = piezas.some(r => String(r._id) === String(asset._id));
      const data = estaIncluida
        ? piezas.map(r => ({
            ...r,
            isSelected: String(r._id) === String(asset._id)
          }))
        : [{ ...mapAssetFromApi(asset), isSelected: true }];

      dispatch({ type: OPEN_RIGHTS_VIEWER_SUCCESS, payload: { data } });
    } catch (err) {
      dispatch({ type: OPEN_RIGHTS_VIEWER_FAILURE, payload: err });
    }
  };
};

export const closeRightsViewer = () => ({ type: CLOSE_RIGHTS_VIEWER });

/**
 * Piezas por vencer que este usuario puede gestionar.
 *
 * No pasa por el store: es una lectura única para el aviso de entrada y nadie
 * más la observa. El backend resuelve el alcance según el rol y los grupos, así
 * que acá no se manda ningún filtro.
 */
export const fetchRightsAlert = workclient =>
  BunkeyAPI({
    url: `/clients/${workclient}/rights/alert`,
    method: "GET"
  }).then(resp => resp.data);
